/**
 * Reads the Aadhaar Secure QR's decimal payload out of a captured image, in the browser.
 *
 * WHY: the Secure QR endpoint accepts either the QR IMAGE (`data`) or the decimal string
 * printed in the QR (`payload`). Sending the payload skips the server's image-decode step
 * entirely, which is the step that fails on a real-world photo — a phone picture of an
 * Aadhaar card is angled, glared and low-contrast, and the server gets one attempt at
 * locating the QR inside it. The phone's own decoder is looking at the same pixels with far
 * better tooling and, unlike the server, can be re-pointed until it succeeds.
 *
 * HOW: the native `BarcodeDetector`, which is present in Chrome and Android WebView — the
 * browsers the applicant journey actually runs in — so this adds NO dependency and no
 * bundle weight. Where it is missing (Safari / iOS), this returns null and the caller sends
 * the image exactly as it does today. That is the whole fallback: nothing degrades, some
 * devices simply take the faster path.
 *
 * The payload is DIGITS ONLY. That is not cosmetic: the gateway's request validation
 * rejects a non-digit payload, and a QR that decodes to anything else is not a Secure QR
 * (an Aadhaar card can carry other codes, and a random QR in frame would otherwise be sent
 * as though it were the applicant's Aadhaar). Returning null for those falls back to the
 * image, where the server can look for the right code itself.
 */

// Minimal shape of the native API — TypeScript's DOM lib does not declare it yet.
interface DetectedBarcodeLike {
  rawValue: string;
  format: string;
}

interface BarcodeDetectorLike {
  detect(source: ImageBitmapSource): Promise<DetectedBarcodeLike[]>;
}

interface BarcodeDetectorConstructor {
  new (options?: { formats?: string[] }): BarcodeDetectorLike;
  getSupportedFormats?(): Promise<string[]>;
}

/** Whether this browser can read a QR without shipping a decoder. */
export function canReadQrLocally(): boolean {
  return typeof window !== 'undefined' && 'BarcodeDetector' in window;
}

/**
 * Whether a LIVE scanner is possible here: a QR decoder plus a camera to point at the code.
 * Both are needed — a decoder with no camera access can still read a photo, which is the
 * fallback, but it cannot scan.
 */
export function canScanQrLive(): boolean {
  return canReadQrLocally() && typeof navigator?.mediaDevices?.getUserMedia === 'function';
}

/**
 * The Secure QR payload visible in a single video frame, or null.
 *
 * Split out from the image path because the scanner calls this several times a second and
 * must not pay for a fetch + blob + createImageBitmap round-trip each time — a
 * `HTMLVideoElement` is itself a valid `ImageBitmapSource`, so the frame is read in place.
 */
export async function detectAadhaarQr(source: ImageBitmapSource): Promise<string | null> {
  const Detector = (window as unknown as { BarcodeDetector?: BarcodeDetectorConstructor })
    .BarcodeDetector;
  if (!Detector) return null;

  try {
    const codes = await new Detector({ formats: ['qr_code'] }).detect(source);
    return firstAadhaarPayload(codes);
  } catch {
    // A frame can fail to decode for entirely normal reasons — mid-resize, not yet painted.
    // The caller simply tries the next one.
    return null;
  }
}

/**
 * The first code that looks like an Aadhaar Secure QR payload.
 *
 * DIGITS ONLY, and long. Both halves matter: the gateway's validation rejects a non-digit
 * payload, and an Aadhaar card or the surface under it can carry other codes — sending a
 * random QR as though it were the applicant's Aadhaar would be worse than not reading it.
 * The length floor rejects short numeric codes; a real payload runs to thousands of digits.
 */
function firstAadhaarPayload(codes: readonly DetectedBarcodeLike[]): string | null {
  for (const code of codes) {
    const digits = code.rawValue?.trim() ?? '';
    if (digits.length > 100 && /^\d+$/.test(digits)) {
      return digits;
    }
  }

  return null;
}

/**
 * The Secure QR's decimal payload from a data-URL image, or null when it could not be read
 * (unsupported browser, no QR found, or a QR that is not the Aadhaar payload).
 *
 * Never throws: every failure is a null, because the caller's fallback — send the image —
 * is always available and a decode failure is not an error the applicant should see.
 */
export async function readAadhaarQrPayload(imageDataUrl: string): Promise<string | null> {
  const Detector = (window as unknown as { BarcodeDetector?: BarcodeDetectorConstructor })
    .BarcodeDetector;
  if (!Detector) return null;

  try {
    const blob = await (await fetch(imageDataUrl)).blob();
    const bitmap = await createImageBitmap(blob);
    try {
      return firstAadhaarPayload(await new Detector({ formats: ['qr_code'] }).detect(bitmap));
    } finally {
      // Explicit: an ImageBitmap holds decoded pixels until it is closed, and a few
      // full-resolution phone photos is real memory on a low-end device.
      bitmap.close();
    }
  } catch {
    return null;
  }
}
